/**
 * Frontend env access — central typed wrapper.
 *
 * ── Doctrine (Feb 27, 2026, operator-locked) ─────────────────────
 *
 * Direct `process.env.*` reads in NEW code are forbidden. Every
 * new env consumer should go through this module. Existing call
 * sites are grandfathered via the allowlist in
 * `/app/backend/tests/test_no_raw_process_env_scanner.py` — they
 * MAY be migrated opportunistically when the file is touched for
 * other reasons, but DO NOT refactor in bulk.
 *
 * Why centralise?
 *   • One place to enforce defaults + validation.
 *   • One place to swap config sources (env → fetched config,
 *     etc.) without crawling the whole app.
 *   • Prevents the recurring "config is set correctly but the
 *     consumer hard-coded a fallback" bug class.
 *
 * Approved exits from this module:
 *   • `apiBase()`        — REACT_APP_BACKEND_URL
 *   • `nodeEnv()`        — process.env.NODE_ENV (dev|test|prod)
 *   • `isDevelopment()`  — convenience boolean
 *   • `isProduction()`   — convenience boolean
 *
 * Feature flags own their own typed wrappers (one module per
 * flag — see `lib/mediaFeatureFlag.js` for the pattern). New
 * flags MUST follow that pattern.
 *
 * NOTE: `process.env` reads in this file are the ONLY legitimate
 * raw reads on the frontend. The scanner whitelists this file.
 */

function _envOrThrow(name) {
  const v = process.env[name];
  if (!v) {
    throw new Error(
      `Missing required env var ${name}. Fails fast — see ` +
      `frontend/src/lib/env.js doctrine header.`,
    );
  }
  return v;
}

/** Canonical platform API base URL. Fails fast if unset. */
export function apiBase() {
  return _envOrThrow("REACT_APP_BACKEND_URL");
}

/** Raw NODE_ENV. Returns "development" | "production" | "test". */
export function nodeEnv() {
  return (process.env.NODE_ENV || "production").toLowerCase();
}

export function isDevelopment() {
  return nodeEnv() === "development";
}

export function isProduction() {
  return nodeEnv() === "production";
}

/**
 * True when the app is running against the LIVE production domain
 * (nationaldrivingguild.co.uk) — distinct from a compiled preview
 * build that also has NODE_ENV=production. Used to hide dev-only
 * controls that must never surface to real users in production.
 *
 * Heuristic (belt + braces):
 *   • window.location.hostname does NOT contain "preview" /
 *     "emergent.host" / "localhost" / "127.0.0.1"
 *   • AND REACT_APP_BACKEND_URL matches the same rule
 * If either signal indicates a non-production surface, we return
 * false (fail open — dev controls SHOW when unsure).
 */
export function isLiveProductionHost() {
  const nonProdMarkers = ["preview", "emergent.host", "localhost", "127.0.0.1"];
  const host = (typeof window !== "undefined" && window.location && window.location.hostname) || "";
  const be = process.env.REACT_APP_BACKEND_URL || "";
  const hostIsProd = host && !nonProdMarkers.some((m) => host.includes(m));
  const beIsProd = be && !nonProdMarkers.some((m) => be.includes(m));
  return Boolean(hostIsProd && beIsProd);
}
